"use client";

import { useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { AccountingPlanColumn } from "./AccountingPlanColumn";
import type { AccountingPlan } from "./pricing";

/**
 * The three accounting tiers, side by side from lg and a one-at-a-time carousel
 * below it: three full-width cards stacked is a lot of scrolling, and side by
 * side they'd get ~200px each.
 *
 * Every card is still rendered and simply hidden, so no breakpoint has to be
 * measured in JS — and because each column carries its own feature list,
 * stepping through the cards swaps "What's included" with them for free.
 */
export function AccountingPlanPicker({
  plans,
  selectedId,
  onSelect,
  tickClassName,
}: {
  plans: AccountingPlan[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  tickClassName?: string;
}) {
  // Which tier the carousel is showing. Separate from the selection: stepping
  // through the cards browses them, it doesn't choose one. Starts on whichever
  // tier is already selected so returning to this step doesn't hide the
  // customer's own choice behind an arrow.
  const selectedIndex = plans.findIndex((plan) => plan.id === selectedId);
  const [active, setActive] = useState(selectedIndex === -1 ? 0 : selectedIndex);

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full items-center justify-between gap-4 lg:hidden">
        <button
          type="button"
          onClick={() => setActive((i) => Math.max(0, i - 1))}
          disabled={active === 0}
          aria-label="Previous plan"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0F1319] text-white outline outline-1 -outline-offset-1 outline-white/15 transition-opacity disabled:opacity-40"
        >
          <ChevronLeftIcon aria-hidden className="h-5 w-5" />
        </button>

        <span
          aria-live="polite"
          className="min-w-0 truncate text-center font-inter text-sm font-medium text-[#9CA3AF]"
        >
          {plans[active].name}{" "}
          <span className="text-[#6B7280]">
            ({active + 1}/{plans.length})
          </span>
        </span>

        <button
          type="button"
          onClick={() => setActive((i) => Math.min(plans.length - 1, i + 1))}
          disabled={active === plans.length - 1}
          aria-label="Next plan"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0F1319] text-white outline outline-1 -outline-offset-1 outline-white/15 transition-opacity disabled:opacity-40"
        >
          <ChevronRightIcon aria-hidden className="h-5 w-5" />
        </button>
      </div>

      <fieldset className="flex w-full flex-col gap-8 sm:gap-10 lg:flex-row lg:items-start lg:gap-2">
        <legend className="sr-only">Choose an accounting plan</legend>
        {plans.map((plan, index) => (
          <div
            key={plan.id}
            className={cn("w-full lg:block lg:flex-1", index === active ? "block" : "hidden")}
          >
            <AccountingPlanColumn
              plan={plan}
              selected={selectedId === plan.id}
              onSelect={onSelect}
              tickClassName={tickClassName}
            />
          </div>
        ))}
      </fieldset>
    </div>
  );
}
